(() => {
  if (customElements.get('similar-products')) return;

  class SimilarProducts extends HTMLElement {
    static get observedAttributes() { return ['vendor-sku']; }

    constructor() {
      super();
      this.renderVersion = 0;
      this.onProductsReady = () => this.render();
    }

    connectedCallback() {
      this.classList.add('hidden');
      window.addEventListener('theme:product:loaded', this.onProductsReady);
      if (window.theme?.product) this.render();
    }

    disconnectedCallback() {
      this.renderVersion++;
      window.removeEventListener('theme:product:loaded', this.onProductsReady);
      this.renderKey = null;
    }

    attributeChangedCallback(name, oldValue, newValue) {
      if (oldValue !== newValue && this.isConnected && window.theme?.product) this.render();
    }

    normalize(value) {
      return String(value || '').toLowerCase().replace(/[^a-z0-9]/g, '');
    }

    matchScore(product) {
      if (this.dataset.matching !== 'true') return 0;
      const tags = (product.tags || []).map(tag => String(tag).toLowerCase());
      const metal = this.normalize(this.dataset.metal);
      const stones = (this.dataset.stones || '').split('|').map(value => this.normalize(value)).filter(Boolean);
      const coloredStones = stones.filter(value => !value.includes('diamond'));
      const preferredStones = coloredStones.length ? coloredStones : stones;
      const sameMetal = metal && tags.some(tag => tag.startsWith('metal:') && this.normalize(tag.slice(tag.indexOf(':') + 1)) === metal);
      const sameStone = preferredStones.length && tags.some(tag => /^(gemstone|stone|product-center-stone-name):/.test(tag) && preferredStones.some(stone => this.normalize(tag.slice(tag.indexOf(':') + 1)).includes(stone)));
      return (sameMetal ? 2 : 0) + (sameStone ? 4 : 0);
    }

    async render() {
      const skus = [...new Set((this.getAttribute('vendor-sku') || '').split(',').map(value => value.trim()).filter(Boolean))];
      const key = skus.join(',');
      if (key === this.renderKey) return;
      this.renderKey = key;
      const version = ++this.renderVersion;
      this.replaceChildren();
      this.classList.add('hidden');
      if (!skus.length) return;

      const results = await Promise.allSettled(skus.map(async sku => {
        const products = await window.theme.product.getProductHandlesByTag('vendor-sku: ' + sku, 10);
        const target = this.normalize(sku);
        return products.find(product => (product.tags || []).some(tag => /^(vendor-sku|vendor sku):/i.test(tag) && this.normalize(tag.slice(tag.indexOf(':') + 1)) === target));
      }));
      if (!this.isConnected || version !== this.renderVersion) return;

      const seen = new Set([this.dataset.currentHandle]);
      const products = results.flatMap((result, index) => {
        const product = result.status === 'fulfilled' && result.value;
        if (!product?.handle || seen.has(product.handle)) return [];
        seen.add(product.handle);
        return [{ ...product, index, score: this.matchScore(product) }];
      }).sort((a, b) => b.score - a.score || a.index - b.index);
      if (!products.length) return;

      const slider = document.createElement('product-slide');
      slider.dataset.title = this.dataset.title || '';
      for (const product of products) {
        const item = document.createElement('product-slide-item');
        item.dataset.handle = product.handle;
        slider.appendChild(item);
      }
      this.classList.remove('hidden', 'opacity-0');
      this.replaceChildren(slider);
    }
  }

  customElements.define('similar-products', SimilarProducts);
})();
